import type {
  ImpactSolutionType,
  ImpactType,
  MetricAssignmentType,
  MetricValueType,
} from '~/types/types'

import {
  abbreviateNumber,
  roundToNearestNonZeroFractionDigit,
} from '~/assets/helper/functions'

function round(num, decimalPlaces) {
  /**
   * Rounding in JS is inconsistent.

  function naiveRound(num, decimalPlaces) {
      var p = Math.pow(10, decimalPlaces);
      return Math.round(num * p) / p;
  }
  console.log( naiveRound(1.245, 2) );  // 1.25 correct (rounded as expected)
  console.log( naiveRound(1.255, 2) );  // 1.25 incorrect (should be 1.26)

  1.005 in decimal system is encoded to 1.0049999999999999 in 64-bit binary float;
  while, 1234567.005 in decimal system is encoded to 1234567.0049999998882413 in 64-bit binary float

  As such, we need to offset the implicit round-off error using Number.EPSILON

  // test rounding of half
  console.log( round(0.5, 0) );  // 1
  console.log( round(-0.5, 0) ); // -1

  // testing edge cases
  console.log( round(1.005, 2) );  // 1.01
  console.log( round(2.175, 2) );  // 2.18
  console.log( round(5.015, 2) );  // 5.02
  */
  var p = Math.pow(10, decimalPlaces);
  var e = Number.EPSILON * num * p;
  return Math.round((num * p) + e) / p;
}

export const roundValue = (val) => {
  /**
   * Dynamic rounding based on size of input
   */
  if (val === null || val === undefined || val === '') return 0
  if (typeof val === 'string') {
    const parsed = parseFloat(val)
    if (isNaN(parsed)) return 0
    val = parsed
  }
  if (typeof val !== 'number') return 0

  if (Math.abs(val) < 1) return roundToNearestNonZeroFractionDigit(val) // Small numbers, until the first non-zero decimal place
  if (Math.abs(val) < 10) return round(val, 2) // Less than 10, round to 2 decimals
  if (Math.abs(val) < 1000) return round(val, 1) // Less than 1000, round to nearest 1 decimal
  if (Math.abs(val) >= 1000) return Math.round(val) // Over 1000, round to nearest digit
  return round(val, 2) // Fallback for numbers >= 1 and < 10 get max 2 decimal places
}

const GHG_EMISSIONS_MITIGATED_METRIC_ID = 1217

export function ghgSolutionMetricAssignment(
  impactSolution: ImpactSolutionType,
  climateImpact: ImpactType,
): MetricAssignmentType | undefined {
  return climateImpact.metricAssignments?.find(
    (metricAssignment: MetricAssignmentType) =>
      metricAssignment?.metric?.id === GHG_EMISSIONS_MITIGATED_METRIC_ID
      && metricAssignment.ghgSavingPotentialId === impactSolution.ghgSavingPotentialId,
  )
}

export function impactSolutionMetricValues(
  impactSolution: ImpactSolutionType,
  climateImpact: ImpactType,
) {
  return (
    ghgSolutionMetricAssignment(impactSolution, climateImpact)?.metricValues.filter(
      (metricValue: MetricValueType) => metricValue.target !== null,
    ) ?? []
  )
}

export function ghgImpactSolutions(climateImpact: ImpactType): ImpactSolutionType[] {
  return (climateImpact.impactSolutions ?? []).filter(
    (solution) =>
      solution.ghgSavingPotentialId != null && solution.useGhgSavingPotentialTool,
  )
}

export function dateRange(metricValues: Array<MetricValueType>) {
  return metricValues.map((e) => e.period).sort()
}

export function totalGhgSavingsOfImpactSolution(
  metricValues: Array<MetricValueType>,
  locale: string,
) {
  if (!metricValues || !metricValues.length) return '0'

  let total = metricValues.reduce((acc, metricValue) => {
    return metricValue?.target ? acc + metricValue.target : acc
  }, 0)

  return abbreviateNumber(total, locale)
}

export function formatComparativeUnitForPledge(comparativeUnit: string): string {
  return comparativeUnit
    .trim()
    .replace(/^the functional unit is\s+/i, '')
    .replace(/^functional unit is\s+/i, '')
    .replace(/^the\s+(?=use of\s)/i, '')
}

export type GhgChartSolutionSeries = {
  name: string
  metricValues: MetricValueType[]
}

export function ghgChartPeriods(
  solutionSeries: GhgChartSolutionSeries[],
  fallbackMetricValues?: MetricValueType[] | null,
): string[] {
  const periods = new Set<string>()
  for (const series of solutionSeries) {
    for (const metricValue of series.metricValues) {
      if (metricValue.period != null) periods.add(String(metricValue.period))
    }
  }
  if (!periods.size && fallbackMetricValues) {
    for (const metricValue of fallbackMetricValues) {
      if (metricValue.period != null) periods.add(String(metricValue.period))
    }
  }
  return [...periods].sort()
}

export function ghgMetricValuesForPeriods(
  metricValues: MetricValueType[],
  periods: string[],
): number[] {
  const byPeriod = new Map(
    metricValues.map((metricValue) => [
      String(metricValue.period),
      roundValue(metricValue.target ?? 0),
    ]),
  )
  return periods.map((period) => byPeriod.get(period) ?? 0)
}
